import { Button, Group, Image, Loader, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import logoImage from '@/assets/images/Logo-light-punchout.png'
import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import * as classes from '@/styles/SponsorSelectionScreen.css'
import { showChannelSwitchError } from '@/utils/sponsors'

// Color palette for sponsor identification
const SPONSOR_COLORS = [
  '#FF0000', // Red
  '#FF4000', // Vermilion
  '#FF8000', // Orange
  '#FFBF00', // Amber
  '#FFFF00', // Yellow
  '#80FF00', // Chartreuse
  '#40FF00', // Lime Green
  '#00FF00', // Green
  '#00FF80', // Spring Green
  '#00FFFF', // Cyan
  '#0080FF', // Sky Blue
  '#0000FF', // Blue
  '#4000FF', // Indigo
  '#8000FF', // Violet
  '#FF00FF', // Magenta
  '#FF00BF', // Fuchsia
  '#FF0080', // Crimson
  '#FF0040', // Rose
  '#BF0030', // Deep Red
  '#FF3030', // Scarlet
]

// Derive color from sponsor ID
function getSponsorColor(sponsorId: string): string {
  // Convert sponsor ID to a number by summing character codes
  const hash = sponsorId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  // Use modulo to get an index in the color array
  const colorIndex = hash % SPONSOR_COLORS.length
  return SPONSOR_COLORS[colorIndex]
}

export const SponsorSelectionScreen = observer(function SponsorSelectionScreen() {
  const { authenticationStore, uiStore, api } = useGlobalState()
  const [selectedSponsorId, setSelectedSponsorId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)

  // AVAILABLE SPONSORS: All sponsors associated with this user's email (any role)
  // This comes from the login response and includes sponsors where the user might have
  // roles like "End User" that don't grant portal access
  const availableSponsors = authenticationStore.availableSponsors || []

  // ACCESSIBLE SPONSORS: Only sponsors where user has portal access (SME or Sponsor Admin roles)
  // The sponsor-list endpoint filters to only return sponsors where the user can access the portal
  const sponsorQuery = useQuery({
    queryKey: ['sponsor-list', api.baseUrl],
    queryFn: () => api.getSponsorList(),
    enabled: availableSponsors.length > 0 && !isLoading,
  })

  // Build a set of ACCESSIBLE sponsor IDs (those with portal access from the API response)
  // This will be used to filter down the AVAILABLE sponsors list
  const accessibleSponsorIds = new Set<string>()
  const sponsorNameMap = new Map<string, string>()
  if (sponsorQuery.data?.sponsors) {
    sponsorQuery.data.sponsors.forEach(sponsor => {
      accessibleSponsorIds.add(sponsor.sponsorId)
      sponsorNameMap.set(sponsor.sponsorId, sponsor.sponsorName)
    })
  }

  // FILTERED SPONSORS: The intersection of available and accessible
  // Show only sponsors that are both associated with the user's email AND grant portal access
  const filteredSponsors = availableSponsors.filter(sponsor => accessibleSponsorIds.has(sponsor.sponsorId))

  // Build display list with all derived properties computed upfront
  const displaySponsors = filteredSponsors
    .map(sponsor => ({
      sponsorId: sponsor.sponsorId,
      environmentName: sponsor.environmentName,
      targetUrls: sponsor.targetUrls,
      displayName: sponsorNameMap.get(sponsor.sponsorId) || sponsor.environmentName,
      color: getSponsorColor(sponsor.sponsorId),
      isSelected: selectedSponsorId === sponsor.sponsorId,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName))

  // Auto-select if only one sponsor
  useEffect(() => {
    if (filteredSponsors.length === 1 && !selectedSponsorId && !isLoading) {
      setSelectedSponsorId(filteredSponsors[0].sponsorId)
    }
  }, [filteredSponsors, selectedSponsorId, isLoading])

  const handleSponsorSelect = async () => {
    const selectedSponsor = filteredSponsors.find(sponsor => sponsor.sponsorId === selectedSponsorId)
    if (!selectedSponsor) return

    const channelName = sponsorNameMap.get(selectedSponsor.sponsorId) || selectedSponsor.environmentName

    setIsLoading(true)
    uiStore.showLoadingOverlay('relative')

    try {
      const response = await authenticationStore.selectSponsor(selectedSponsor)

      if (!response.ok) {
        console.error('Failed to select sponsor:', response.message, response.error)
        showChannelSwitchError(channelName, response)
      }
    } catch (error) {
      console.error('Error selecting sponsor:', error)
      showChannelSwitchError(channelName, error)
    } finally {
      setIsLoading(false)
      uiStore.hideLoadingOverlay()
    }
  }

  const handleCancel = async () => {
    setIsCancelling(true)
    await authenticationStore.logOut()
    // No need to reset isCancelling as user will be redirected
  }

  return (
    <Stack gap="xl" w="100%">
      <Image src={logoImage} alt="Sidekick Platform" fit="contain" w="100%" h="auto" />
      <Stack gap="sm">
        <Title order={1} size="h6" ta="center" fw={500}>
          Program Manager and Expert Portal
        </Title>

        <Stack gap="md">
          <Text size="sm" fw={500}>
            Select a Channel
          </Text>

          {sponsorQuery.isPending ? (
            <Stack align="center" gap="md" py="xl">
              <Loader size="md" />
              <Text size="sm" c="dimmed">
                Loading organization information...
              </Text>
            </Stack>
          ) : sponsorQuery.isError ? (
            <Stack align="center" gap="md" py="xl">
              <Text size="sm" c="red">
                Unable to load organization information
              </Text>
              <Text size="xs" c="dimmed">
                Please try again or contact support
              </Text>
            </Stack>
          ) : displaySponsors.length === 0 ? (
            <Stack align="center" gap="md" py="xl">
              <Text size="sm" c="red">
                No organizations available
              </Text>
              <Text size="xs" c="dimmed">
                You don&apos;t have portal access to any organizations
              </Text>
            </Stack>
          ) : (
            <Stack gap="xs">
              {displaySponsors.map(sponsor => (
                <UnstyledButton
                  key={sponsor.sponsorId}
                  onClick={() => !isLoading && setSelectedSponsorId(sponsor.sponsorId)}
                  disabled={isLoading}
                  className={theme.cx(classes.sponsorCard, sponsor.isSelected && classes.sponsorCardSelected)}>
                  <Group gap="md" wrap="nowrap">
                    {/* <Box
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: sponsor.color,
                        flexShrink: 0,
                      }}
                    /> */}
                    <Text size="md" fw={500} style={{ flex: 1 }}>
                      {sponsor.displayName}
                    </Text>
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>

      <Group justify="space-between" mt="md">
        <Button variant="subtle" onClick={() => void handleCancel()} disabled={isLoading} loading={isCancelling}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleSponsorSelect()}
          disabled={!selectedSponsorId || isLoading || isCancelling}
          loading={isLoading}>
          Continue
        </Button>
      </Group>
    </Stack>
  )
})
